import { Button, Dropdown, Tooltip } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { FilterItem } from "models/types";
import "./filter-chip.scss";
import {
  CloseOutlined,
  CloseSquareOutlined,
  DownOutlined,
  UpOutlined
} from "@ant-design/icons";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

type FilterChipProps = {
  items: FilterItem[];
  onChipDelete: (item: { field: string; value: string }) => void;
  onCategoryDelete: (item: FilterItem) => void;
  resetAll: () => void;
  nonClearableFields?: string[];
  /**
   * Opt-in: show a date filter's own range on the chip instead of its raw
   * option key. Off by default so existing grids render exactly as before.
   */
  showDateRangeOnChip?: boolean;
};

/**
 * Chip text for a non-set filter. The model carries an option key ("inRange"),
 * which grids can opt out of showing in favour of the range itself.
 */
function describeFilter(item: FilterItem, showDateRange: boolean): string {
  const { dateFrom, dateTo } = item.filter as {
    dateFrom?: string | null;
    dateTo?: string | null;
  };
  if (showDateRange && dateFrom && dateTo) {
    const from = DateUtils.dateTimeObj(dateFrom).format(DATE_FORMAT_MMDDYYYY);
    const to = DateUtils.dateTimeObj(dateTo).format(DATE_FORMAT_MMDDYYYY);
    return `${from} - ${to}`;
  }
  return `${item.filter?.type || ""}  ${item.filter?.filter || ""} ${
    item.filter?.filterTo ? ` to ${item.filter?.filterTo}` : ""
  } `;
}

function FilterValueChip({
  field,
  filterKey,
  item,
  onDelete,
  renderChip,
  clearable
}: {
  field: string;
  filterKey: any;
  item: FilterItem;
  onDelete: (payload: { field: string; value: string }) => void;
  renderChip: (item: FilterItem, key: any) => ReactNode;
  clearable: boolean;
}) {
  return (
    <div
      key={`k-${filterKey}`}
      className="flex flex-wrap gap-y-1 items-center custom-filter-values"
    >
      <div className="flex-1 truncate">{renderChip(item, filterKey)}</div>
      {clearable && (
        <CloseOutlined
          className="ml-2 mt-[1px] text-xs"
          style={{ fontSize: 10, color: "gray" }}
          onClick={() => onDelete({ field, value: filterKey })}
        />
      )}
    </div>
  );
}

function FilterChips(props: FilterChipProps) {
  const {
    items,
    onChipDelete,
    onCategoryDelete,
    resetAll,
    nonClearableFields = [],
    showDateRangeOnChip = false
  } = props;
  const [filterItem, setFilterItem] = useState([] as Array<FilterItem>);
  const [openDropdown, setOpenDropdown] = useState(false);
  useEffect(() => {
    if (!items?.length) {
      setOpenDropdown(false);
      setFilterItem([] as Array<any>);
      return;
    }
    const filters = [] as Array<FilterItem>;
    // `items` is guaranteed non-empty by the `!items?.length` guard above, so the `|| []` fallback is unreachable
    /* v8 ignore next */
    (items || []).forEach((item) => {
      const colObj = {
        field: item?.field,
        header: item?.header,
        filter: item?.filter
      };
      filters.push(colObj);
    });
    setFilterItem(filters);
  }, [items]);
  const handleDelete = (item: any) => {
    onChipDelete(item);
  };

  const renderChip = (item: FilterItem, key: any) => {
    if (key === "") {
      return '""';
    }
    return key === null ? "(Blanks)" : key;
  };

  if (!filterItem.length) return null;

  return (
    <Dropdown
      data-testid="filter-chips-dropdown"
      placement="bottom"
      menu={{ items: [] }}
      popupRender={() => (
        <div className="custom-filter-options" data-testid="index-dropdown">
          {filterItem.map((item, index) => {
            const isNonClearable = nonClearableFields.includes(item.field);
            return (
              <div
                key={item.header || item.field}
                data-testid={`filter-chip-item${index}`}
              >
                <div className="custom-filter-header font-semibold mt-2 flex justify-between items-center">
                  {item.header}
                  {!isNonClearable && (
                    <CloseSquareOutlined
                      className="rounded-lg text-ciq-red-700"
                      style={{ fontSize: 15 }}
                      onClick={() => onCategoryDelete(item)}
                      data-testid={`filter-chip-clear-category-${item.field}`}
                    />
                  )}
                </div>
                <div className="custom-filter-options__container">
                  {item.filter?.filterType &&
                  item.filter.filterType !== "set" ? (
                    <div
                      key={`k-${item.filter?.type}`}
                      className="flex flex-wrap gap-y-1 items-center custom-filter-values"
                    >
                      <Tooltip
                        title={
                          item.filter?.dateTo && item.filter?.dateFrom
                            ? `From: ${DateUtils.dateTimeObj(
                                item.filter.dateFrom
                              ).format(
                                DATE_FORMAT_MMDDYYYY
                              )} To: ${DateUtils.dateTimeObj(
                                item.filter?.dateTo
                              ).format(DATE_FORMAT_MMDDYYYY)}`
                            : ""
                        }
                      >
                        <div className="flex-1 truncate">
                          {describeFilter(item, showDateRangeOnChip)}
                        </div>
                      </Tooltip>
                    </div>
                  ) : (
                    item?.filter?.values?.map((key: any) => (
                      <FilterValueChip
                        key={`k-${key}`}
                        field={item.field}
                        filterKey={key}
                        item={item}
                        onDelete={handleDelete}
                        renderChip={renderChip}
                        clearable={!isNonClearable}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
          {filterItem.some(
            (item) => !nonClearableFields.includes(item.field)
          ) && (
            <div className="flex w-full justify-end custom-filter-divider pt-2">
              <Button
                className="text-ciq-red-700 border-ciq-red-700"
                onClick={() => {
                  items
                    .filter((item) => !nonClearableFields.includes(item.field))
                    .forEach((item) => {
                      onCategoryDelete(item);
                    });
                  resetAll();
                }}
                data-testid="index-button-clear-all"
              >
                Clear All
              </Button>
            </div>
          )}
        </div>
      )}
      className="linking-container--nav__select"
      trigger={["click"]}
      open={openDropdown}
      onOpenChange={(open) => {
        setOpenDropdown(open);
      }}
    >
      <Button className="flex flex-wrap gap-y-1 items-center custom-filter-bar">
        Filter
        <div className="custom-filter-count bg-ciq-primary-900">
          {filterItem.length}
        </div>
        {openDropdown ? (
          <UpOutlined className="text-xs" />
        ) : (
          <DownOutlined className="text-xs" />
        )}
      </Button>
    </Dropdown>
  );
}

export default FilterChips;
